import { useState } from 'react';
import { findDuplicates, formatMoney, type Transaction } from '@wis/shared';
import { useApp } from '../state/AppContext.js';
import { deleteTransaction, restoreTransaction, updateTransaction } from '../db/repository.js';
import { Button, Notice, Sheet, sourceLabel } from '../components/primitives.js';
import { QuickEntryForm, type QuickEntryValues } from '../components/QuickEntryForm.js';

export interface TransactionSheetProps {
  transaction: Transaction | null;
  onClose: () => void;
}

/** Edit or delete one record. Deleting is soft and always offers an undo. */
export function TransactionSheet({ transaction, onClose }: TransactionSheetProps): JSX.Element {
  const { categories, accounts, rules, merchantHistory, settings, transactions, toast } = useApp();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const duplicates = transaction ? findDuplicates(transaction, transactions) : [];

  async function handleSubmit(values: QuickEntryValues): Promise<void> {
    if (!transaction) return;
    await updateTransaction(transaction.id, values);
    toast('Saved');
    onClose();
  }

  async function handleDelete(): Promise<void> {
    if (!transaction) return;
    const { id } = transaction;
    await deleteTransaction(id);
    setConfirmingDelete(false);
    onClose();
    toast('Deleted', {
      label: 'Undo',
      onAction: () => {
        void restoreTransaction(id);
      },
    });
  }

  return (
    <Sheet open={Boolean(transaction)} title="Edit payment" onClose={onClose}>
      {transaction ? (
        <>
          {duplicates.length > 0 ? (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Notice tone="warning">
                Possible duplicate of {formatMoney(duplicates[0]?.transaction.amountSen ?? 0)} at{' '}
                {duplicates[0]?.transaction.merchantNormalized || 'no merchant'},{' '}
                {duplicates[0]?.minutesApart} minute(s) apart. Delete whichever is wrong.
              </Notice>
            </div>
          ) : null}

          <p className="small faint" style={{ marginBottom: 'var(--space-4)' }}>
            {sourceLabel(transaction.source)}
            {transaction.merchantOriginal &&
            transaction.merchantOriginal !== transaction.merchantNormalized
              ? ` · originally "${transaction.merchantOriginal}"`
              : ''}
            {transaction.importConfidence < 1
              ? ` · read with ${Math.round(transaction.importConfidence * 100)}% confidence`
              : ''}
          </p>

          <QuickEntryForm
            key={transaction.id}
            categories={categories}
            accounts={accounts}
            rules={rules}
            history={merchantHistory}
            timeZone={settings.timeZone}
            defaultAccountLabel={settings.defaultAccountLabel}
            defaultCategoryId={settings.defaultCategoryId}
            currency={transaction.currency}
            autoFocusAmount={false}
            initial={{
              amountSen: transaction.amountSen,
              type: transaction.type,
              merchant: transaction.merchantOriginal || transaction.merchantNormalized,
              categoryId: transaction.categoryId,
              accountLabel: transaction.accountLabel,
              occurredAt: transaction.occurredAt,
              note: transaction.note ?? '',
            }}
            submitLabel="Save changes"
            onSubmit={handleSubmit}
            footer={
              <div style={{ marginTop: 'var(--space-5)' }}>
                {confirmingDelete ? (
                  <div className="stack">
                    <Notice tone="danger">
                      Delete this payment? It will stop counting towards your totals.
                    </Notice>
                    <div className="row">
                      <Button
                        variant="quiet"
                        className="grow"
                        onClick={() => setConfirmingDelete(false)}
                      >
                        Keep it
                      </Button>
                      <Button variant="danger" className="grow" onClick={handleDelete}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="danger" block onClick={() => setConfirmingDelete(true)}>
                    Delete payment
                  </Button>
                )}
              </div>
            }
          />
        </>
      ) : null}
    </Sheet>
  );
}
